import _ from "lodash";
import { connect } from "react-redux";
import React from "react";
import UIBaseComponent from "UIComponents/UIBaseComponent";
import I18nHOC from "UIComponents/I18nHOC";
import classes from "./DateDurationSelector.scss";
import { compose } from "lib/apolloClient";
import moment from "moment";
import { DatePicker, IconButton, Tooltip } from "@toddle-edu/ds-web";
import classNames from "classnames";
import {
  CalendarScheduleOutlined,
  SettingsOutlined,
  CalendarCrossOutlined,
} from "@toddle-edu/ds-icons";
import { getUserInfo } from "Login/modules/LoginModule";
import DateSelectorModal from "./DateSelectorModal";
import SelectedDatesPopover from "./SelectedDatesPopover";
import withDerivedProps from "lib/withDerivedProps";
import { getSelectedAcademicYearId } from "modules/CommonSelectors";
import { isUserTypeStaff } from "Utils";
class DateDurationSelector extends UIBaseComponent {
  constructor(props) {
    super(props);
    this.state = {
      showDatePickerModal: false,
      showPopover: { state: false, activeKey: "" },
      updatedDates: {
        startDate: props.value?.startDate || null,
        endDate: props.value?.endDate || null,
      },
    };
    this.nodeRef = {};
    this.startDateLabelContainerRef = React.createRef();
    this.endDateLabelContainerRef = React.createRef();
  }

  componentDidMount = () => {
    const {
      value: { endDate, startDate },
      maxDate,
      minDate,
    } = this.props;

    // Clamp startDate within [minDate, maxDate]
    let clampedStartDate = startDate;
    if (
      !moment(startDate).isValid() ||
      moment(startDate).isBefore(moment(minDate))
    ) {
      clampedStartDate = minDate;
    } else if (moment(startDate).isAfter(moment(maxDate))) {
      clampedStartDate = maxDate;
    }

    // Clamp endDate within [minDate, maxDate]
    let clampedEndDate = endDate;
    if (
      !moment(endDate).isValid() ||
      moment(endDate).isAfter(moment(maxDate))
    ) {
      clampedEndDate = maxDate;
    } else if (moment(endDate).isBefore(moment(minDate))) {
      clampedEndDate = minDate;
    }

    // Ensure endDate >= startDate
    if (moment(clampedEndDate).isBefore(moment(clampedStartDate))) {
      clampedEndDate = clampedStartDate;
    }

    const hasStartDateChanged = clampedStartDate !== startDate;
    const hasEndDateChanged = clampedEndDate !== endDate;

    if (hasStartDateChanged || hasEndDateChanged) {
      this.changeDate({
        ...(hasStartDateChanged && { startDate: clampedStartDate }),
        ...(hasEndDateChanged && { endDate: clampedEndDate }),
      });
    }

    // Register label containers with parent
    const {
      registerLabelContainerRef,
      fieldKey,
      shouldRegisterLabelContainerRef,
    } = this.props;
    if (shouldRegisterLabelContainerRef && registerLabelContainerRef) {
      registerLabelContainerRef(
        `${fieldKey}-start-date`,
        this.startDateLabelContainerRef
      );
      registerLabelContainerRef(
        `${fieldKey}-end-date`,
        this.endDateLabelContainerRef
      );
    }
  };

  componentDidUpdate(prevProps) {
    const { value } = this.props;
    const { value: prevValue } = prevProps;

    // Update updatedDates state when props.value changes
    if (
      value.startDate !== prevValue.startDate ||
      value.endDate !== prevValue.endDate
    ) {
      this.setState({
        updatedDates: {
          startDate: value.startDate,
          endDate: value.endDate,
        },
      });
    }
  }

  componentWillUnmount() {
    const {
      registerLabelContainerRef,
      fieldKey,
      shouldRegisterLabelContainerRef,
    } = this.props;
    if (shouldRegisterLabelContainerRef && registerLabelContainerRef) {
      registerLabelContainerRef(`${fieldKey}-start-date`, null);
      registerLabelContainerRef(`${fieldKey}-end-date`, null);
    }
  }

  shouldShowViewEmpty = () => {
    return false;
  };

  handleDatePickerModal = () => {
    this.setState(prevState => ({
      showDatePickerModal: !prevState.showDatePickerModal,
    }));
  };

  changeDate = params => {
    const {
      value,
      maxDate,
      minNumOfDays,
      shouldChangeEndDateWithStartDate,
      minDate,
    } = this.props;

    const { endDate } = value;

    if (
      _.has(params, "startDate") &&
      moment(params.startDate).isBefore(moment(minDate))
    ) {
      params["startDate"] = minDate;
    }

    if (
      _.has(params, "startDate") &&
      moment(params.startDate).isAfter(moment(endDate))
    ) {
      params["endDate"] = moment(params.startDate).add(minNumOfDays, "days");
    }

    // When we need the end Date to be made start Date + minNumOfDays, automatically when startDate is changed
    //        shouldChangeEndDateWithStartDate = true
    //        the number of days difference between dates = minNumOfDays
    //
    if (_.has(params, "startDate") && shouldChangeEndDateWithStartDate) {
      params["endDate"] = moment(params.startDate).add(minNumOfDays, "days");
    }

    if (
      _.has(params, "endDate") &&
      moment(params.endDate).isAfter(moment(maxDate))
    ) {
      params["endDate"] = maxDate;
    }

    // Format start and end dates to "YYYY-MM-DD"
    if (_.has(params, "startDate")) {
      params["startDate"] = moment(params.startDate).format("YYYY-MM-DD");
    }
    if (_.has(params, "endDate")) {
      params["endDate"] = moment(params.endDate).format("YYYY-MM-DD");
    }

    // Update the updatedDates state
    this.setState(prevState => ({
      updatedDates: {
        ...prevState.updatedDates,
        ...params,
      },
    }));

    this.updateValue({ ...value, ...params });
  };

  isValid = () => {
    // check if both startDate and endDate are valid and endDate is after startDate
    const {
      value: { startDate, endDate },
    } = this.props;
    return !(
      moment(startDate).isValid() &&
      moment(endDate).isValid() &&
      moment(endDate).isAfter(moment(startDate))
    );
  };

  DateDuration = () => {
    const {
      t,
      minDate,
      maxDate,
      startDateName,
      endDateName,
      startDatePlaceholder,
      endDatePlaceholder,
      value: { startDate, endDate },
      testId,
      mode,
      learningCourseId,
      classDates,
      courseClassList,
      isLearningCourseEnabled,
      isDifferentStartDatesSelected,
      isDifferentEndDatesSelected,
      differentStartDatePlaceholder,
      differentEndDatePlaceholder,
      isBlueprintCourse,
    } = this.props;

    const disableDateSelection = _.isEmpty(courseClassList);
    const isEditDisabled = mode === "view" ? true : false;
    if (isLearningCourseEnabled) {
      return (
        <div className={classes.container}>
          <div className={classes.dateContainer}>
            <div
              className={classNames("text-label-l", classes.label)}
              ref={this.startDateLabelContainerRef}
            >
              <CalendarScheduleOutlined size="xx-small" />
              <span>{t("common:start_date")}</span>
            </div>
            {isDifferentStartDatesSelected ? (
              <div
                className={classNames(
                  "text-body",
                  classes.dateSelectorPlaceholder
                )}
                onClick={this.handleDatePickerModal}
              >
                {t(differentStartDatePlaceholder)}
              </div>
            ) : (
              <div className={classes.datePickerContainer}>
                <DatePicker
                  onChange={date => this.changeDate({ [startDateName]: date })}
                  size="small"
                  format="DD MMM YYYY"
                  value={moment(this.state.updatedDates.startDate || startDate)}
                  minDate={moment(minDate)}
                  maxDate={moment(maxDate)}
                  disabled={isEditDisabled}
                  dsVersion="2.0"
                  variant="inline"
                  placeholder={
                    _.isEqual(startDatePlaceholder, "common:start_date")
                      ? t(startDatePlaceholder)
                      : startDatePlaceholder
                  }
                  testId={`${testId}-startDate`}
                  showIcon={false}
                />
              </div>
            )}
            {isLearningCourseEnabled && !isBlueprintCourse && (
              <div className={classes.datePickerIcon}>
                <Tooltip
                  align={{ offset: [32, 0] }}
                  placement="bottomRight"
                  tooltip={
                    disableDateSelection
                      ? t("unitPlan:date_selector_disabled_msg")
                      : t("unitPlan:date_selector_tooltip_msg")
                  }
                  showArrow={true}
                >
                  <IconButton
                    icon={<SettingsOutlined />}
                    variant="neutral"
                    type="fill"
                    size="medium"
                    dsVersion="2.0"
                    isCompact={true}
                    onClick={this.handleDatePickerModal}
                    disabled={disableDateSelection}
                  />
                </Tooltip>
              </div>
            )}
          </div>
          <div className={classes.dateContainer}>
            <div
              className={classNames("text-label-l", classes.label)}
              ref={this.endDateLabelContainerRef}
            >
              <CalendarCrossOutlined size="xx-small" />
              <span> {t("common:end_date")}</span>
            </div>
            {isDifferentEndDatesSelected ? (
              <div
                className={classNames(
                  "text-body",
                  classes.dateSelectorPlaceholder
                )}
                onClick={this.handleDatePickerModal}
              >
                {t(differentEndDatePlaceholder)}
              </div>
            ) : (
              <div className={classes.datePickerContainer}>
                <DatePicker
                  onChange={date =>
                    this.changeDate({
                      [endDateName]: date.format("YYYY-MM-DD"),
                    })
                  }
                  size="small"
                  format="DD MMM YYYY"
                  variant="inline"
                  value={moment(this.state.updatedDates.endDate || endDate)}
                  minDate={moment(startDate)}
                  maxDate={moment(maxDate)}
                  disabled={isEditDisabled}
                  dsVersion="2.0"
                  placeholder={
                    _.isEqual(endDatePlaceholder, "common:end_date")
                      ? t(endDatePlaceholder)
                      : endDatePlaceholder
                  }
                  startDate={moment(startDate)}
                  testId={`${testId}-endDate`}
                  showIcon={false}
                />
              </div>
            )}
            {isLearningCourseEnabled && !isBlueprintCourse && (
              <div className={classes.datePickerIcon}>
                <Tooltip
                  align={{ offset: [32, 0] }}
                  placement="bottomRight"
                  tooltip={
                    disableDateSelection
                      ? t("unitPlan:date_selector_disabled_msg")
                      : t("unitPlan:date_selector_tooltip_msg")
                  }
                  showArrow={true}
                >
                  <IconButton
                    icon={<SettingsOutlined />}
                    variant="neutral"
                    type="fill"
                    size="medium"
                    dsVersion="2.0"
                    isCompact={true}
                    onClick={this.handleDatePickerModal}
                    disabled={disableDateSelection}
                  />
                </Tooltip>
              </div>
            )}
          </div>
          {this.state.showDatePickerModal && (
            <DateSelectorModal
              onClose={this.handleDatePickerModal}
              learningCourseId={learningCourseId}
              startDate={startDate}
              endDate={endDate}
              minDate={minDate}
              maxDate={maxDate}
              onDateChange={this.updateValue}
              classDates={classDates}
              courseClassList={courseClassList}
            />
          )}
        </div>
      );
    }

    return (
      <div className={classes.container}>
        {/* Start Date */}
        <div className={classes.dateContainer}>
          <div
            className={classNames("text-label-l", classes.label)}
            ref={this.startDateLabelContainerRef}
          >
            <CalendarScheduleOutlined size="xx-small" />
            <span>{t("common:start_date")}</span>
          </div>
          <div className={classes.datePickerContainer}>
            <DatePicker
              onChange={date => this.changeDate({ [startDateName]: date })}
              size="small"
              format="DD MMM YYYY"
              value={moment(this.state.updatedDates.startDate || startDate)}
              minDate={moment(minDate)}
              maxDate={moment(maxDate)}
              disabled={isEditDisabled}
              dsVersion="2.0"
              variant="inline"
              placeholder={
                _.isEqual(startDatePlaceholder, "common:start_date")
                  ? t(startDatePlaceholder)
                  : startDatePlaceholder
              }
              testId={`${testId}-startDate`}
              showIcon={false}
            />
          </div>
        </div>

        {/* End Date */}
        <div className={classes.dateContainer}>
          <div
            className={classNames("text-label-l", classes.label)}
            ref={this.endDateLabelContainerRef}
          >
            <CalendarCrossOutlined size="xx-small" />
            <span> {t("common:end_date")}</span>
          </div>
          <div className={classes.datePickerContainer}>
            <DatePicker
              onChange={date =>
                this.changeDate({ [endDateName]: date.format("YYYY-MM-DD") })
              }
              size="small"
              format="DD MMM YYYY"
              variant="inline"
              value={moment(this.state.updatedDates.endDate || endDate)}
              minDate={moment(startDate)}
              maxDate={moment(maxDate)}
              disabled={isEditDisabled}
              dsVersion="2.0"
              placeholder={
                _.isEqual(endDatePlaceholder, "common:end_date")
                  ? t(endDatePlaceholder)
                  : endDatePlaceholder
              }
              startDate={moment(startDate)}
              testId={`${testId}-endDate`}
              showIcon={false}
            />
          </div>
        </div>
      </div>
    );
  };

  handleMouseHandler = ({ node, activeKey }) => {
    this.nodeRef[activeKey] = node;
    this.setState(prevState => {
      const prevShowPopover = prevState.showPopover || {};

      return {
        showPopover: {
          state: !prevShowPopover.state,
          activeKey: activeKey,
        },
      };
    });
  };

  DateDurationView = () => {
    const {
      t,
      value: { startDate, endDate },
      isLearningCourseEnabled,
      isDifferentStartDatesSelected,
      isDifferentEndDatesSelected,
      differentStartDatePlaceholder,
      differentEndDatePlaceholder,
      courseClassList,
      classDates,
      isStaff,
      courseId,
    } = this.props;
    const { showPopover } = this.state;

    // get start and end date from classDates or unitPlan
    let updatedStartDate = startDate;
    let updatedEndDate = endDate;
    const selectedClass = _.find(classDates, { classId: courseId });

    if (!_.isEmpty(classDates) && !isStaff) {
      updatedStartDate = _.get(selectedClass, "startDate", startDate);
      updatedEndDate = _.get(selectedClass, "endDate", endDate);
    }

    if (isLearningCourseEnabled) {
      const viewModeStartDateClasses = classNames({
        "text-body": true,
        [classes.dateSelectorPlaceholderView]: true,
        [classes.dateSelectorPlaceholderViewOnHover]: _.isEqual(
          showPopover.activeKey,
          "startDate"
        ),
      });
      const viewModeEndDateClasses = classNames({
        "text-body": true,
        [classes.dateSelectorPlaceholderView]: true,
        [classes.dateSelectorPlaceholderViewOnHover]: _.isEqual(
          showPopover.activeKey,
          "endDate"
        ),
      });

      return (
        <>
          <div className={classes.viewContainer}>
            <div className={classes.dateContainer}>
              <div
                className={classNames("text-label-l", classes.label)}
                ref={this.startDateLabelContainerRef}
              >
                <CalendarScheduleOutlined size="xx-small" />
                <span>{t("common:start_date")}</span>
              </div>
              {isDifferentStartDatesSelected && isStaff ? (
                <div
                  className={viewModeStartDateClasses}
                  ref={ref => (this.nodeRef["startDate"] = ref)}
                  onClick={() =>
                    this.handleMouseHandler({
                      node: this.nodeRef["startDate"],
                      activeKey: "startDate",
                    })
                  }
                >
                  {t(differentStartDatePlaceholder)}
                </div>
              ) : (
                <div className={classes.dateDurationLabel}>
                  <span className="text-body">
                    {moment(updatedStartDate).format("DD MMM YYYY")}
                  </span>
                </div>
              )}
            </div>
            <div className={classes.dateContainer}>
              <div
                className={classNames("text-label-l", classes.label)}
                ref={this.endDateLabelContainerRef}
              >
                <CalendarCrossOutlined size="xx-small" />
                <span> {t("common:end_date")}</span>
              </div>
              {isDifferentEndDatesSelected && isStaff ? (
                <div
                  className={viewModeEndDateClasses}
                  ref={ref => (this.nodeRef["endDate"] = ref)}
                  onClick={() =>
                    this.handleMouseHandler({
                      node: this.nodeRef["endDate"],
                      activeKey: "endDate",
                    })
                  }
                >
                  {t(differentEndDatePlaceholder)}
                </div>
              ) : (
                <div className={classes.dateDurationLabel}>
                  <span className="text-body">
                    {moment(updatedEndDate).format("DD MMM YYYY")}
                  </span>
                </div>
              )}
            </div>
          </div>
          {showPopover.state && (
            <SelectedDatesPopover
              courseClassList={courseClassList}
              classDates={classDates}
              nodeRef={this.nodeRef[showPopover.activeKey]}
              isOpen={showPopover.state}
              handlePopoverClose={() =>
                this.handleMouseHandler({
                  node: this.nodeRef[showPopover.activeKey],
                  activeKey: "",
                  state: false,
                })
              }
            />
          )}
        </>
      );
    }

    return (
      <div className={classes.viewContainer}>
        <div className={classes.dateContainer}>
          <div
            className={classNames("text-label-l", classes.label)}
            ref={this.startDateLabelContainerRef}
          >
            <CalendarScheduleOutlined size="xx-small" />
            <span>{t("common:start_date")}</span>
          </div>

          <div className={classes.dateDurationLabel}>
            <span className="text-body">
              {moment(startDate).format("DD MMM YYYY")}
            </span>
          </div>
        </div>
        <div className={classes.dateContainer}>
          <div
            className={classNames("text-label-l", classes.label)}
            ref={this.endDateLabelContainerRef}
          >
            <CalendarCrossOutlined size="xx-small" />
            <span> {t("common:end_date")}</span>
          </div>

          <div className={classes.dateDurationLabel}>
            <span className="text-body">
              {moment(endDate).format("DD MMM YYYY")}
            </span>
          </div>
        </div>
      </div>
    );
  };

  renderEdit = () => this.DateDuration();
  renderView = () => this.DateDurationView();
}

DateDurationSelector.defaultProps = {
  ...UIBaseComponent.defaultProps,
  minDate: null,
  maxDate: null,
  isDisabled: false,
  value: { startDate: null, endDate: null },
  startDateLabel: "common:start_date",
  endDateLabel: "common:end_date",
  startDateName: "startDate",
  endDateName: "endDate",
  startDatePlaceholder: "common:start_date",
  endDatePlaceholder: "common:end_date",
  minNumOfDays: 40,
  shouldChangeEndDateWithStartDate: false,
  differentStartDatePlaceholder: "unitPlan:start_date_selector_placeholder_msg",
  differentEndDatePlaceholder: "unitPlan:end_date_selector_placeholder_msg",
};

DateDurationSelector.propTypes = {
  ...UIBaseComponent.propTypes,
};

DateDurationSelector.displayName = "DateDurationSelector";

const mapStateToProps = state => {
  const academicYearId = getSelectedAcademicYearId(state);
  const courseId = _.get(state, "teacher.selected_class.selected_course", "");
  return {
    academicYearId,
    courseId,
  };
};

const getDerivedProps = ownProps => {
  const userInfo = getUserInfo();
  const { user_type: userType } = userInfo;
  const isStaff = isUserTypeStaff({ userType });

  const { learningCourseId, classDates, unitPlanCourses } = ownProps;
  const isLearningCourseEnabled = !_.isNil(learningCourseId);

  const isDifferentStartDatesSelected =
    !_.isEmpty(classDates) &&
    !_.every(
      classDates,
      date => date.startDate === _.head(classDates)?.startDate
    );
  const isDifferentEndDatesSelected =
    !_.isEmpty(classDates) &&
    !_.every(classDates, date => date.endDate === _.head(classDates)?.endDate);

  return {
    courseClassList: unitPlanCourses,
    isLearningCourseEnabled,
    isDifferentStartDatesSelected,
    isDifferentEndDatesSelected,
    isStaff,
  };
};

export default compose(
  I18nHOC({ resource: ["common", "unitPlan"] }),
  connect(mapStateToProps),
  withDerivedProps(getDerivedProps)
)(DateDurationSelector);
